import { useEffect, useMemo, useState } from "react";
import {
  IconArrowLeft,
  IconDeviceFloppy,
  IconPlus,
  IconTicket,
  IconTrash,
} from "@tabler/icons-react";
import { useFormik } from "formik";
import * as yup from "yup";
import InlineError from "@components/forms/InlineError";
import AppModal from "@components/modals/AppModal";
import {
  deleteStartPointPromoCodeRequest,
  listStartPointPromoCodesRequest,
  saveStartPointPromoCodeRequest,
} from "@services/startPoints/startPointPromoCodesApi";

function createPromoCodeDraft() {
  return {
    id: "",
    clientId: `promo-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    code: "",
    name: "",
    isActive: true,
    percentage: "",
    maxUses: "",
    usedCount: 0,
    remainingUses: null,
    appleProductId: "",
    googleProductId: "",
  };
}

const promoCodeSchema = yup.object({
  code: yup.string().trim().required("Code is required."),
  name: yup.string().trim().required("Name is required."),
  isActive: yup.boolean().required("Status is required."),
  percentage: yup
    .number()
    .typeError("Percentage is required.")
    .required("Percentage is required.")
    .min(0, "Percentage must be 0 or more.")
    .max(100, "Percentage must be 100 or less."),
  maxUses: yup
    .number()
    .transform((value, originalValue) =>
      originalValue === "" || originalValue === null ? null : value,
    )
    .nullable()
    .integer("Maximum uses must be a whole number.")
    .min(1, "Maximum uses must be at least 1."),
  appleProductId: yup.string().when("percentage", {
    is: (value) => Number(value) < 100,
    then: (schema) => schema.trim().required("Apple product id is required."),
    otherwise: (schema) => schema.trim(),
  }),
  googleProductId: yup.string().when("percentage", {
    is: (value) => Number(value) < 100,
    then: (schema) => schema.trim().required("Google product id is required."),
    otherwise: (schema) => schema.trim(),
  }),
});

function normalizePromoCode(item) {
  const promoSource =
    item?.promo_code && typeof item.promo_code === "object"
      ? item.promo_code
      : item;
  return {
    id: promoSource?.id || "",
    clientId:
      item?.clientId ||
      promoSource?.id ||
      `promo-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    code: promoSource?.code || "",
    name: promoSource?.name || "",
    isActive:
      typeof promoSource?.isActive === "boolean"
        ? promoSource.isActive
        : typeof promoSource?.is_active === "boolean"
          ? promoSource.is_active
          : typeof promoSource?.active === "boolean"
            ? promoSource.active
          : true,
    percentage:
      promoSource?.percentage === 0 || promoSource?.percentage
        ? String(promoSource.percentage)
        : "",
    maxUses:
      promoSource?.maxUses === 0 || promoSource?.maxUses
        ? String(promoSource.maxUses)
        : promoSource?.max_uses === 0 || promoSource?.max_uses
          ? String(promoSource.max_uses)
          : "",
    usedCount: Number(promoSource?.usedCount ?? promoSource?.used_count ?? 0),
    remainingUses:
      promoSource?.remainingUses ?? promoSource?.remaining_uses ?? null,
    appleProductId:
      promoSource?.appleProductId || promoSource?.apple_product_id || "",
    googleProductId:
      promoSource?.googleProductId || promoSource?.google_product_id || "",
  };
}

function normalizePercentageInput(value) {
  if (value === "" || value === null || value === undefined) return "";
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return "";
  if (numericValue < 0) return 0;
  if (numericValue > 100) return 100;
  return numericValue;
}

function normalizeMaxUsesInput(value) {
  if (value === "" || value === null || value === undefined) return "";
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return "";
  return Math.max(0, Math.trunc(numericValue));
}

export function StartPointPromoCodesButton({ onClick }) {
  return (
    <button
      type="button"
      className="app-icon-btn app-icon-btn--update start-point-tool-promo-action"
      aria-label="Manage promo codes"
      title="Manage promo codes"
      onClick={onClick}
    >
      <IconTicket size={14} />
    </button>
  );
}

export function StartPointPromoCodesModal({
  isOpen,
  startPointId,
  pointName,
  onClose,
}) {
  const [promoCodes, setPromoCodes] = useState([]);
  const [selectedPromoId, setSelectedPromoId] = useState("");
  const [isCreatingPromoCode, setIsCreatingPromoCode] = useState(false);
  const [fetchingPromoCodes, setFetchingPromoCodes] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [localError, setLocalError] = useState("");

  const selectedPromo = useMemo(() => {
    const current = promoCodes.find(
      (item) => (item.id || item.clientId || "") === selectedPromoId,
    );
    if (current) return current;
    if (!selectedPromoId) return createPromoCodeDraft();
    return {
      ...createPromoCodeDraft(),
      clientId: selectedPromoId,
    };
  }, [promoCodes, selectedPromoId]);

  const formik = useFormik({
    enableReinitialize: true,
    validateOnMount: true,
    initialValues: selectedPromo,
    validationSchema: promoCodeSchema,
    onSubmit: () => {},
  });

  useEffect(() => {
    if (!isOpen) {
      setPromoCodes([]);
      setSelectedPromoId("");
      setIsCreatingPromoCode(false);
      setFetchingPromoCodes(false);
      setSubmitting(false);
      setLocalError("");
      return;
    }

    setSelectedPromoId("");
    setIsCreatingPromoCode(false);
    setLocalError("");
  }, [isOpen, startPointId]);

  useEffect(() => {
    if (!isOpen || !startPointId) return;
    let isMounted = true;

    async function fetchPromoCodes() {
      setFetchingPromoCodes(true);
      setLocalError("");
      try {
        const response = await listStartPointPromoCodesRequest(startPointId);
        if (!isMounted) return;
        setPromoCodes(
          (Array.isArray(response?.body) ? response.body : []).map(normalizePromoCode),
        );
      } catch (error) {
        if (!isMounted) return;
        setLocalError(error?.message || "Could not load promo codes.");
      } finally {
        if (isMounted) setFetchingPromoCodes(false);
      }
    }

    fetchPromoCodes();
    return () => {
      isMounted = false;
    };
  }, [isOpen, startPointId]);

  const hasPercentageValue =
    formik.values.percentage !== "" &&
    formik.values.percentage !== null &&
    formik.values.percentage !== undefined;
  const requiresProductIds =
    hasPercentageValue && Number(formik.values.percentage) < 100;

  useEffect(() => {
    if (requiresProductIds) return;
    if (!formik.values.appleProductId && !formik.values.googleProductId) return;
    formik.setFieldValue("appleProductId", "", false);
    formik.setFieldValue("googleProductId", "", false);
  }, [
    formik,
    formik.values.appleProductId,
    formik.values.googleProductId,
    requiresProductIds,
  ]);

  function getFieldError(fieldName) {
    return formik.touched?.[fieldName] ? formik.errors?.[fieldName] || "" : "";
  }

  function handleAddPromoCode() {
    const nextPromo = createPromoCodeDraft();
    setSelectedPromoId(nextPromo.clientId);
    setIsCreatingPromoCode(true);
    setLocalError("");
  }

  function handleEditPromoCode(promo) {
    setSelectedPromoId(promo.id || promo.clientId || "");
    setIsCreatingPromoCode(false);
    setLocalError("");
  }

  function handleBackToIndex() {
    if (submitting) return;
    setSelectedPromoId("");
    setIsCreatingPromoCode(false);
    setLocalError("");
  }

  async function refreshPromoCodesList() {
    if (!startPointId) return;
    const response = await listStartPointPromoCodesRequest(startPointId);
    setPromoCodes(
      (Array.isArray(response?.body) ? response.body : []).map(normalizePromoCode),
    );
  }

  function handleSavePromoCode() {
    formik.setTouched(
      {
        code: true,
        name: true,
        isActive: true,
        percentage: true,
        maxUses: true,
        appleProductId: true,
        googleProductId: true,
      },
      true,
    );

    formik.validateForm().then(async (errors) => {
      if (Object.keys(errors || {}).length || !startPointId) return;

      setSubmitting(true);
      setLocalError("");

      const nextPromo = {
        id: String(formik.values.id || "").trim(),
        code: String(formik.values.code || "").trim().toUpperCase(),
        name: String(formik.values.name || "").trim(),
        isActive: Boolean(formik.values.isActive),
        percentage: Number(formik.values.percentage),
        maxUses:
          formik.values.maxUses === "" || formik.values.maxUses === null
            ? null
            : Number(formik.values.maxUses),
        appleProductId: requiresProductIds
          ? String(formik.values.appleProductId || "").trim()
          : "",
        googleProductId: requiresProductIds
          ? String(formik.values.googleProductId || "").trim()
          : "",
      };

      console.log("start-point promo codes payload", {
        id: startPointId || "",
        promo_code: {
          id: nextPromo.id || "",
          code: nextPromo.code,
          name: nextPromo.name,
          is_active: nextPromo.isActive,
          percentage: nextPromo.percentage,
          max_uses: nextPromo.maxUses,
          apple_product_id: nextPromo.appleProductId,
          google_product_id: nextPromo.googleProductId,
        },
      });

      try {
        await saveStartPointPromoCodeRequest(startPointId, nextPromo);
        await refreshPromoCodesList();
        setSelectedPromoId("");
        setIsCreatingPromoCode(false);
      } catch (error) {
        setLocalError(error?.message || "Could not save promo code.");
      } finally {
        setSubmitting(false);
      }
    });
  }

  async function handleDeletePromoCode(id) {
    if (!startPointId || !id) return;
    setSubmitting(true);
    setLocalError("");
    try {
      await deleteStartPointPromoCodeRequest(startPointId, id);
      await refreshPromoCodesList();
      setSelectedPromoId("");
      setIsCreatingPromoCode(false);
    } catch (error) {
      setLocalError(error?.message || "Could not delete promo code.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AppModal
      isOpen={isOpen}
      onClose={onClose}
      title={`Promo Codes - ${pointName || startPointId || ""}`}
      size="lg"
      className="start-point-promo-modal-shell"
    >
      <div className="start-point-promo-modal">
        {fetchingPromoCodes ? <p className="form-helper">Loading promo codes...</p> : null}
        {localError ? <p className="message-error">{localError}</p> : null}

        {!fetchingPromoCodes && !isCreatingPromoCode && !selectedPromoId ? (
          <div className="start-point-promo-toolbar">
            <button
              type="button"
              className="app-icon-btn app-icon-btn--add"
              onClick={handleAddPromoCode}
              aria-label="Add promo code"
              title="Add promo code"
              disabled={submitting}
            >
              <IconPlus size={16} />
            </button>
          </div>
        ) : null}

        {!fetchingPromoCodes &&
        !isCreatingPromoCode &&
        !selectedPromoId &&
        promoCodes.length ? (
          <div className="start-point-promo-list">
            {promoCodes.map((promo) => (
              <div key={promo.id || promo.clientId} className="start-point-promo-card">
                <button
                  type="button"
                  className="start-point-promo-card-main"
                  onClick={() => handleEditPromoCode(promo)}
                >
                  <strong>
                    {promo.code} · {promo.percentage || 0}%
                  </strong>
                  <span>
                    {promo.name} · {promo.isActive ? "Active" : "Deactivated"}
                  </span>
                  <small>
                    {promo.maxUses === ""
                      ? "Unlimited uses"
                      : `${promo.usedCount || 0}/${promo.maxUses} uses · ${promo.remainingUses ?? Math.max(0, Number(promo.maxUses) - Number(promo.usedCount || 0))} remaining`}
                  </small>
                </button>
                <button
                  type="button"
                  className="app-icon-btn app-icon-btn--delete"
                  onClick={() => handleDeletePromoCode(promo.id || "")}
                  aria-label="Delete promo code"
                  title="Delete promo code"
                  disabled={submitting}
                >
                  <IconTrash size={14} />
                </button>
              </div>
            ))}
          </div>
        ) : null}

        {!fetchingPromoCodes &&
        !isCreatingPromoCode &&
        !selectedPromoId &&
        !promoCodes.length ? (
          <div className="start-point-promo-list" />
        ) : null}

        {(isCreatingPromoCode || selectedPromoId) && !fetchingPromoCodes ? (
          <div className="start-point-promo-form">
            <div className="start-point-promo-form-top">
              <button
                type="button"
                className="app-icon-btn app-icon-btn--back"
                onClick={handleBackToIndex}
                aria-label="Back to promo codes"
                title="Back to promo codes"
                disabled={submitting}
              >
                <IconArrowLeft size={16} />
              </button>
            </div>

            <div className="tour-add-form-group">
              <p className="tour-add-form-label">
                code
                {getFieldError("code") ? <InlineError>{getFieldError("code")}</InlineError> : null}
              </p>
              <label className="field-label app-form-field">
                <input
                  className="field-input"
                  value={formik.values.code}
                  onChange={(event) =>
                    formik.setFieldValue(
                      "code",
                      String(event.target.value || "").toUpperCase(),
                      true,
                    )
                  }
                  onBlur={() => formik.setFieldTouched("code", true, true)}
                />
              </label>
            </div>

            <div className="tour-add-form-group">
              <p className="tour-add-form-label">
                maximum uses
                {getFieldError("maxUses") ? (
                  <InlineError>{getFieldError("maxUses")}</InlineError>
                ) : null}
              </p>
              <label className="field-label app-form-field">
                <input
                  type="number"
                  min="1"
                  step="1"
                  className="field-input"
                  value={formik.values.maxUses}
                  placeholder="Unlimited"
                  onChange={(event) =>
                    formik.setFieldValue(
                      "maxUses",
                      normalizeMaxUsesInput(event.target.value),
                      true,
                    )
                  }
                  onBlur={() => formik.setFieldTouched("maxUses", true, true)}
                />
              </label>
              <p className="form-helper">
                Leave empty for unlimited uses. Use 1 for a single-use code.
              </p>
            </div>

            <div className="tour-add-form-group">
              <p className="tour-add-form-label">
                name
                {getFieldError("name") ? <InlineError>{getFieldError("name")}</InlineError> : null}
              </p>
              <label className="field-label app-form-field">
                <input
                  className="field-input"
                  value={formik.values.name}
                  onChange={(event) => formik.setFieldValue("name", event.target.value, true)}
                  onBlur={() => formik.setFieldTouched("name", true, true)}
                />
              </label>
            </div>

            <div className="tour-add-form-group">
              <p className="tour-add-form-label">
                status
                {getFieldError("isActive") ? (
                  <InlineError>{getFieldError("isActive")}</InlineError>
                ) : null}
              </p>
              <label className="field-label app-form-field">
                <select
                  className="field-input select-control"
                  value={formik.values.isActive ? "true" : "false"}
                  onChange={(event) =>
                    formik.setFieldValue(
                      "isActive",
                      event.target.value === "true",
                      true,
                    )
                  }
                  onBlur={() => formik.setFieldTouched("isActive", true, true)}
                >
                  <option value="true">Active</option>
                  <option value="false">Deactivated</option>
                </select>
              </label>
            </div>

            <div className="tour-add-form-group">
              <p className="tour-add-form-label">
                percentage
                {getFieldError("percentage") ? (
                  <InlineError>{getFieldError("percentage")}</InlineError>
                ) : null}
              </p>
              <label className="field-label app-form-field">
                <input
                  type="number"
                  min="0"
                  max="100"
                  className="field-input"
                  value={formik.values.percentage}
                  onChange={(event) =>
                    formik.setFieldValue(
                      "percentage",
                      normalizePercentageInput(event.target.value),
                      true,
                    )
                  }
                  onBlur={() => formik.setFieldTouched("percentage", true, true)}
                />
              </label>
            </div>

            {requiresProductIds ? (
              <>
                <div className="tour-add-form-group">
                  <p className="tour-add-form-label">
                    product id Apple
                    {getFieldError("appleProductId") ? (
                      <InlineError>{getFieldError("appleProductId")}</InlineError>
                    ) : null}
                  </p>
                  <label className="field-label app-form-field">
                    <input
                      className="field-input"
                      value={formik.values.appleProductId}
                      onChange={(event) =>
                        formik.setFieldValue("appleProductId", event.target.value, true)
                      }
                      onBlur={() =>
                        formik.setFieldTouched("appleProductId", true, true)
                      }
                    />
                  </label>
                </div>

                <div className="tour-add-form-group">
                  <p className="tour-add-form-label">
                    product id Google
                    {getFieldError("googleProductId") ? (
                      <InlineError>{getFieldError("googleProductId")}</InlineError>
                    ) : null}
                  </p>
                  <label className="field-label app-form-field">
                    <input
                      className="field-input"
                      value={formik.values.googleProductId}
                      onChange={(event) =>
                        formik.setFieldValue("googleProductId", event.target.value, true)
                      }
                      onBlur={() =>
                        formik.setFieldTouched("googleProductId", true, true)
                      }
                    />
                  </label>
                </div>
              </>
            ) : null}

            <div className="start-point-promo-save-row">
              <button
                type="button"
                className={`app-icon-btn app-icon-btn--add mode-route-tool-route-btn ${!formik.isValid || submitting ? "is-disabled" : ""} ${submitting ? "is-loading" : ""}`}
                onClick={handleSavePromoCode}
                disabled={!formik.isValid || submitting}
                aria-disabled={!formik.isValid || submitting}
                aria-label="Save promo code"
                title="Save promo code"
              >
                {submitting ? (
                  <span className="mode-route-tool-btn-spinner" />
                ) : (
                  <IconDeviceFloppy size={16} />
                )}
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </AppModal>
  );
}
